import React, { useMemo, useState } from 'react';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useReorderCategories,
  useArchiveCategory,
  useRestoreCategory,
  useDeleteCategory,
} from '../hooks/useCategories';
import { useArticleCollections } from '../hooks/useArticleCollections';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/apiErrors';
import { cn } from '@/lib/utils';
import {
  Plus,
  Edit,
  Trash2,
  FolderTree,
  Search,
  AlertCircle,
  X,
  Archive,
  ArchiveRestore,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  ChevronDown,
  EyeOff,
  BookOpen,
} from 'lucide-react';
import { CategorySettingsSkeleton } from './LoadingSkeleton';
import { TreeSelect } from './tree-select/TreeSelect';
import { collectionToOption, type TreeOption } from './tree-select/treeOptions';
import { MAX_CATEGORY_DEPTH } from '../types/tickets';
import type { CategoryDetailResponse, CreateCategoryRequest, UpdateCategoryRequest } from '../types/tickets';

interface CategoryFormData {
  name: string;
  description: string;
  parentCategoryId: string | null;
  isVisibleInPortal: boolean;
  suggestedCollectionId: string | null;
}

interface FormErrors {
  name?: string;
  description?: string;
}

interface CategorySettingsProps {
  organizationId?: string;
}

const EMPTY_FORM: CategoryFormData = {
  name: '',
  description: '',
  parentCategoryId: null,
  isVisibleInPortal: true,
  suggestedCollectionId: null,
};

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

// Mirrors CategoryService.DeleteCategoryAsync: only a category nothing uses can be deleted; a used one is archived.
// The server also counts deleted tickets and automation rules, so it has the final say.
function getDeleteBlockedReason(category: CategoryDetailResponse): string | null {
  if (category.childCount > 0) {
    return `This category has ${plural(category.childCount, 'subcategory', 'subcategories')}, so it can't be deleted. ` +
      'Move or delete them first' + (category.archivedAt ? '.' : ', or archive it instead.');
  }
  if (category.ticketCount > 0) {
    return `This category is used by ${plural(category.ticketCount, 'ticket', 'tickets')}, so it can't be deleted.` +
      (category.archivedAt ? '' : ' Archive it instead.');
  }
  return null;
}

export const CategorySettings: React.FC<CategorySettingsProps> = ({ organizationId }) => {
  // Management lists archived categories too, where they sit in the tree
  const { data: categories = [], isLoading, error } = useCategories(organizationId, { includeArchived: true });
  const { data: collections = [] } = useArticleCollections(organizationId);
  const createCategoryMutation = useCreateCategory(organizationId);
  const updateCategoryMutation = useUpdateCategory(organizationId);
  const reorderCategoriesMutation = useReorderCategories(organizationId);
  const archiveCategoryMutation = useArchiveCategory(organizationId);
  const restoreCategoryMutation = useRestoreCategory(organizationId);
  const deleteCategoryMutation = useDeleteCategory(organizationId);

  const [searchTerm, setSearchTerm] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryDetailResponse | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const byId = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const childrenOf = useMemo(() => {
    const map = new Map<string | null, CategoryDetailResponse[]>();
    for (const c of categories) {
      const key = c.parentCategoryId ?? null;
      map.set(key, [...(map.get(key) ?? []), c]);
    }
    return map;
  }, [categories]);

  // The category and everything under it
  const subtreeOf = (id: string): Set<string> => {
    const result = new Set<string>([id]);
    const stack = [id];
    while (stack.length) {
      const current = stack.pop()!;
      for (const child of childrenOf.get(current) ?? []) {
        if (!result.has(child.id)) {
          result.add(child.id);
          stack.push(child.id);
        }
      }
    }
    return result;
  };

  // Levels from the category down to its deepest subcategory (1 without children)
  const subtreeHeight = (id: string): number => {
    const children = childrenOf.get(id) ?? [];
    return children.length === 0 ? 1 : 1 + Math.max(...children.map((c) => subtreeHeight(c.id)));
  };

  // Parents the dialog may offer: active, not the category itself or below it, and leaving room for its subtree
  const parentOptions: TreeOption[] = useMemo(() => {
    const excluded = editingCategory ? subtreeOf(editingCategory.id) : new Set<string>();
    const height = editingCategory ? subtreeHeight(editingCategory.id) : 1;
    return categories
      .filter((c) => !c.archivedAt && !excluded.has(c.id))
      .map((c) => ({
        id: c.id,
        name: c.name,
        path: c.path,
        depth: c.depth,
        disabled: c.depth + height > MAX_CATEGORY_DEPTH,
        hint: c.depth + height > MAX_CATEGORY_DEPTH ? 'too deep' : undefined,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, editingCategory]);

  const collectionOptions = useMemo(() => collections.map(collectionToOption), [collections]);

  const selectedParent = formData.parentCategoryId ? byId.get(formData.parentCategoryId) : undefined;
  const parentHidesFromCustomers = !!selectedParent && !selectedParent.isEffectivelyVisibleInPortal;

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    const name = formData.name.trim();

    if (!name) {
      errors.name = 'Category name is required';
    } else if (name.length > 100) {
      errors.name = 'Category name must be 100 characters or less';
    } else if (name.includes('›')) {
      errors.name = 'Category names can\'t contain "›"';
    }

    if (formData.description.length > 500) {
      errors.description = 'Description must be 500 characters or less';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openCreateDialog = (parent?: CategoryDetailResponse) => {
    setEditingCategory(null);
    resetForm();
    setFormData({ ...EMPTY_FORM, parentCategoryId: parent?.id ?? null });
    setDialogOpen(true);
  };

  const openEditDialog = (category: CategoryDetailResponse) => {
    setEditingCategory(category);
    setFormErrors({});
    setFormData({
      name: category.name,
      description: category.description ?? '',
      parentCategoryId: category.parentCategoryId ?? null,
      isVisibleInPortal: category.isVisibleInPortal,
      suggestedCollectionId: category.suggestedCollectionId ?? null,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const name = formData.name.trim();
    const description = formData.description.trim();

    try {
      if (editingCategory) {
        // Only what changed: a field left out is kept, null clears it
        const updates: UpdateCategoryRequest = {};
        if (name !== editingCategory.name) updates.name = name;
        if (description !== (editingCategory.description ?? '')) updates.description = description || null;
        if (formData.parentCategoryId !== (editingCategory.parentCategoryId ?? null)) updates.parentCategoryId = formData.parentCategoryId;
        if (formData.isVisibleInPortal !== editingCategory.isVisibleInPortal) updates.isVisibleInPortal = formData.isVisibleInPortal;
        if (formData.suggestedCollectionId !== (editingCategory.suggestedCollectionId ?? null)) updates.suggestedCollectionId = formData.suggestedCollectionId;

        await updateCategoryMutation.mutateAsync({ id: editingCategory.id, updates });
        toast({ title: 'Success', description: 'Category updated successfully.' });
      } else {
        const request: CreateCategoryRequest = {
          name,
          description: description || undefined,
          parentCategoryId: formData.parentCategoryId ?? undefined,
          isVisibleInPortal: formData.isVisibleInPortal,
          suggestedCollectionId: formData.suggestedCollectionId ?? undefined,
        };
        await createCategoryMutation.mutateAsync(request);
        if (formData.parentCategoryId) {
          // Make sure the new subcategory is visible
          setCollapsed((prev) => {
            const next = new Set(prev);
            next.delete(formData.parentCategoryId!);
            return next;
          });
        }
        toast({ title: 'Success', description: 'Category created successfully.' });
      }

      setDialogOpen(false);
      setEditingCategory(null);
      resetForm();
    } catch (error) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, editingCategory ? 'Failed to update category.' : 'Failed to create category.'),
        variant: 'destructive',
      });
    }
  };

  const handleMove = async (category: CategoryDetailResponse, direction: -1 | 1) => {
    const siblings = (childrenOf.get(category.parentCategoryId ?? null) ?? []).filter((c) => !c.archivedAt);
    const index = siblings.findIndex((c) => c.id === category.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= siblings.length) return;

    const orderedIds = siblings.map((c) => c.id);
    [orderedIds[index], orderedIds[target]] = [orderedIds[target], orderedIds[index]];

    try {
      await reorderCategoriesMutation.mutateAsync({ parentCategoryId: category.parentCategoryId ?? null, orderedIds });
    } catch (error) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to reorder categories.'),
        variant: 'destructive',
      });
    }
  };

  const handleArchive = async (category: CategoryDetailResponse) => {
    try {
      await archiveCategoryMutation.mutateAsync(category.id);
      toast({
        title: 'Success',
        description: `"${category.path}" archived${category.childCount > 0 ? ' with its subcategories' : ''}. Tickets that use it keep it.`,
      });
    } catch (error) {
      toast({ title: 'Error', description: getErrorMessage(error, 'Failed to archive category.'), variant: 'destructive' });
    }
  };

  const handleRestore = async (category: CategoryDetailResponse) => {
    try {
      await restoreCategoryMutation.mutateAsync(category.id);
      toast({ title: 'Success', description: `"${category.path}" restored.` });
    } catch (error) {
      toast({ title: 'Error', description: getErrorMessage(error, 'Failed to restore category.'), variant: 'destructive' });
    }
  };

  const handleDelete = async (category: CategoryDetailResponse) => {
    try {
      await deleteCategoryMutation.mutateAsync(category.id);
      toast({ title: 'Success', description: 'Category permanently deleted.' });
    } catch (error) {
      toast({ title: 'Error', description: getErrorMessage(error, 'Failed to delete category.'), variant: 'destructive' });
    }
  };

  const toggleCollapsed = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const searching = searchTerm.trim().length > 0;

  // Browsing: the tree, minus rows under a collapsed category. Searching: every match, shown by path.
  const visibleCategories = useMemo(() => {
    if (searching) {
      const q = searchTerm.trim().toLowerCase();
      return categories.filter((c) =>
        c.path.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q));
    }
    return categories.filter((c) => {
      let parentId = c.parentCategoryId ?? null;
      while (parentId) {
        if (collapsed.has(parentId)) return false;
        parentId = byId.get(parentId)?.parentCategoryId ?? null;
      }
      return true;
    });
  }, [categories, collapsed, byId, searching, searchTerm]);

  const activeCount = categories.filter((c) => !c.archivedAt).length;

  const renderBadges = (category: CategoryDetailResponse) => (
    <>
      {category.archivedAt && (
        <Badge variant="secondary" className="gap-1 text-xs flex-shrink-0">
          <Archive className="h-3 w-3" />
          Archived
        </Badge>
      )}
      {!category.isVisibleInPortal ? (
        <Badge variant="outline" className="gap-1 text-xs flex-shrink-0" title="Not offered to customers, and not shown on their tickets">
          <EyeOff className="h-3 w-3" />
          Internal
        </Badge>
      ) : !category.isEffectivelyVisibleInPortal ? (
        <Badge variant="outline" className="gap-1 text-xs flex-shrink-0 text-muted-foreground" title="A category above it is internal">
          <EyeOff className="h-3 w-3" />
          Internal (inherited)
        </Badge>
      ) : null}
      {category.suggestedCollectionName && (
        <Badge variant="outline" className="gap-1 text-xs flex-shrink-0 font-normal" title="Customers who pick this category are shown articles from this collection">
          <BookOpen className="h-3 w-3" />
          {category.suggestedCollectionName}
        </Badge>
      )}
    </>
  );

  // Reorder, add subcategory, edit, archive/restore and delete
  const renderActions = (category: CategoryDetailResponse) => {
    const deleteBlockedReason = getDeleteBlockedReason(category);
    const activeSiblings = (childrenOf.get(category.parentCategoryId ?? null) ?? []).filter((c) => !c.archivedAt);
    const siblingIndex = activeSiblings.findIndex((c) => c.id === category.id);
    const parent = category.parentCategoryId ? byId.get(category.parentCategoryId) : undefined;
    const parentArchived = !!parent?.archivedAt;
    const canAddChild = !category.archivedAt && category.depth < MAX_CATEGORY_DEPTH;
    const busy = reorderCategoriesMutation.isPending;

    return (
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {!searching && !category.archivedAt && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleMove(category, -1)}
              disabled={busy || siblingIndex <= 0}
              title="Move up"
              aria-label={`Move ${category.name} up`}
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleMove(category, 1)}
              disabled={busy || siblingIndex < 0 || siblingIndex >= activeSiblings.length - 1}
              title="Move down"
              aria-label={`Move ${category.name} down`}
            >
              <ArrowDown className="h-4 w-4" />
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => openCreateDialog(category)}
          disabled={!canAddChild}
          title={canAddChild ? 'Add subcategory' : category.archivedAt ? 'Restore it to add subcategories' : `Categories can be at most ${MAX_CATEGORY_DEPTH} levels deep`}
          aria-label={`Add a subcategory to ${category.name}`}
        >
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => openEditDialog(category)}
          title="Edit"
          aria-label={`Edit ${category.name}`}
        >
          <Edit className="h-4 w-4" />
        </Button>

        {category.archivedAt ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleRestore(category)}
            disabled={restoreCategoryMutation.isPending || parentArchived}
            title={parentArchived ? `Restore "${parent?.path}" first` : 'Restore'}
            aria-label={`Restore ${category.name}`}
          >
            <ArchiveRestore className="h-4 w-4" />
          </Button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={archiveCategoryMutation.isPending}
                title="Archive"
                aria-label={`Archive ${category.name}`}
              >
                <Archive className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive Category</AlertDialogTitle>
                <AlertDialogDescription>
                  Archive "{category.path}"
                  {category.childCount > 0 && <> and its {plural(category.childCount, 'subcategory', 'subcategories')}</>}?
                  {' '}They will no longer be offered when creating or editing tickets, on the customer portal, or to
                  automations and AI classification. Tickets that already use them keep them. You can restore it at
                  any time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => handleArchive(category)}>Archive</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={deleteCategoryMutation.isPending}
              title="Delete"
              aria-label={`Delete ${category.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Category</AlertDialogTitle>
              <AlertDialogDescription>
                Permanently delete "{category.path}"? This can't be undone. Only a category that nothing uses can
                be deleted.
                {deleteBlockedReason && (
                  <span className="text-destructive font-medium">
                    <br />{deleteBlockedReason}
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(category)}
                disabled={deleteBlockedReason !== null}
                className="bg-destructive hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  };

  if (isLoading) {
    return <CategorySettingsSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">{getErrorMessage(error, 'Failed to load categories. Please try again.')}</p>
        </CardContent>
      </Card>
    );
  }

  const saving = createCategoryMutation.isPending || updateCategoryMutation.isPending;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold">Ticket Categories</h3>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Classify tickets for routing and reporting, up to {MAX_CATEGORY_DEPTH} levels deep. Internal categories
            aren't shown to customers. The knowledge base is organized separately, on the Collections tab.
          </p>
        </div>
        <Button onClick={() => openCreateDialog()} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Add Category
        </Button>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center space-x-2">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="relative flex-1 max-w-sm">
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-8"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <FolderTree className="h-4 w-4 sm:h-5 sm:w-5" />
              Categories ({searching ? visibleCategories.length : activeCount})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4 pt-0 sm:pt-0">
            {visibleCategories.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-muted-foreground">
                <FolderTree className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                <p className="text-sm sm:text-base">No categories found</p>
                {!searching && <p className="text-xs sm:text-sm">Create your first category to get started</p>}
              </div>
            ) : (
              <ul className="divide-y" role="tree" aria-label="Ticket categories">
                {visibleCategories.map((category) => {
                  const hasChildren = category.childCount > 0;
                  const isCollapsed = collapsed.has(category.id);
                  return (
                    <li
                      key={category.id}
                      role="treeitem"
                      aria-level={category.depth}
                      aria-expanded={hasChildren && !searching ? !isCollapsed : undefined}
                      className={cn('flex flex-col sm:flex-row sm:items-center gap-2 py-2.5 px-1 sm:px-2', category.archivedAt && 'opacity-70')}
                    >
                      <div
                        className="flex items-start gap-1.5 min-w-0 flex-1"
                        style={{ paddingLeft: searching ? 0 : `${(category.depth - 1) * 24}px` }}
                      >
                        {!searching && hasChildren ? (
                          <button
                            type="button"
                            onClick={() => toggleCollapsed(category.id)}
                            className="mt-0.5 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                            aria-label={isCollapsed ? `Expand ${category.name}` : `Collapse ${category.name}`}
                          >
                            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        ) : (
                          <span className="w-5 flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <span className="font-medium text-sm truncate">{searching ? category.path : category.name}</span>
                            {renderBadges(category)}
                          </div>
                          {category.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{category.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-2 pl-6 sm:pl-0">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="text-xs whitespace-nowrap">
                            {plural(category.ticketCount, 'ticket', 'tickets')}
                          </Badge>
                          {category.openTicketCount > 0 && (
                            <Badge variant="destructive" className="text-xs whitespace-nowrap">
                              {category.openTicketCount} open
                            </Badge>
                          )}
                        </div>
                        {renderActions(category)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create / edit dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingCategory(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : selectedParent ? 'New Subcategory' : 'New Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory
                ? `Changes apply to every ticket in "${editingCategory.path}".`
                : 'Categories classify tickets for routing, reporting and AI triage.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="category-name"
                value={formData.name}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, name: e.target.value }));
                  if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: undefined }));
                }}
                placeholder="Category name"
                className={formErrors.name ? 'border-red-500' : ''}
              />
              <div className="flex justify-between">
                <div>
                  {formErrors.name && (
                    <p className="flex items-center gap-1 text-sm text-red-500">
                      <AlertCircle className="h-4 w-4" />
                      {formErrors.name}
                    </p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{formData.name.length}/100</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category-parent">Parent category</Label>
              <TreeSelect
                id="category-parent"
                options={parentOptions}
                value={formData.parentCategoryId}
                onChange={(id) => setFormData((prev) => ({ ...prev, parentCategoryId: id }))}
                noneLabel="None (top level)"
                placeholder="None (top level)"
                searchPlaceholder="Search categories..."
              />
              {editingCategory && editingCategory.childCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  Its {plural(editingCategory.childCount, 'subcategory moves', 'subcategories move')} with it.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="category-description">Description</Label>
              <Textarea
                id="category-description"
                value={formData.description}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, description: e.target.value }));
                  if (formErrors.description) setFormErrors((prev) => ({ ...prev, description: undefined }));
                }}
                placeholder="Optional. Shown to customers choosing a category, and helps AI classification."
                rows={3}
                className={formErrors.description ? 'border-red-500' : ''}
              />
              <div className="flex justify-between">
                <div>
                  {formErrors.description && (
                    <p className="flex items-center gap-1 text-sm text-red-500">
                      <AlertCircle className="h-4 w-4" />
                      {formErrors.description}
                    </p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{formData.description.length}/500</p>
              </div>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="category-visible" className="font-medium">Visible to customers</Label>
                <p className="text-xs text-muted-foreground">
                  {parentHidesFromCustomers
                    ? `"${selectedParent?.path}" is internal, so this category is hidden from customers too.`
                    : 'Turn off for internal categories (e.g. "Spam", "Escalation"): not offered on the portal and not shown on the customer\'s ticket. Its subcategories are hidden too.'}
                </p>
              </div>
              <Switch
                id="category-visible"
                checked={formData.isVisibleInPortal}
                onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isVisibleInPortal: checked }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category-collection">Suggested knowledge base collection</Label>
              <TreeSelect
                id="category-collection"
                options={collectionOptions}
                value={formData.suggestedCollectionId}
                onChange={(id) => setFormData((prev) => ({ ...prev, suggestedCollectionId: id }))}
                noneLabel="None"
                placeholder="None"
                searchPlaceholder="Search collections..."
                emptyMessage="No collections yet"
              />
              <p className="text-xs text-muted-foreground">
                Customers who pick this category on the portal are shown its articles before they submit.
                Subcategories without their own use this one.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.name.trim() || saving}>
              {saving ? 'Saving...' : editingCategory ? 'Save Changes' : 'Create Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
